import { useMutation, useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import { withJWTClient } from "../connectClient";
import type { PreviewFillParams } from "@/api/dragon-tiles/previewUserContext";
import type { TextComposerPreviewContent } from "@/features/TextComposerV2/preview/useMessagePreview";
import type { SmartOfferCategoryHint } from "@/frames/schemas";
import type {
  ProductGridConfig,
  ProductGridPick,
  SiteId,
} from "@/types/dragon-tiles";

import { previewUserContextKeyParts } from "@/api/dragon-tiles/previewUserContext";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

export type TestSendOffer = {
  fallback_code: string;
  /** Smart Super Offer keys (`smart:<id>`); the backend picks the offer. */
  smart?: { category_hint?: SmartOfferCategoryHint };
};

export type TestSendReviewConfig = {
  maxReviews: number;
  minScore: number;
  verifiedBuyerOnly: boolean;
  sortBy: string;
};

export type TestSendProductGrid = {
  items: string;
  maxProducts: number;
  /**
   * Static grids only. The picks *are* the grid's content — unlike a model
   * grid, the backend cannot derive them from recipient data — so dropping
   * this leaves a test send with nothing to resolve and an empty grid.
   */
  staticProducts?: ProductGridPick[];
  reviews?: TestSendReviewConfig;
};

export type TestSendConfig = {
  previewText: string;
  subjectLine: string;
  offers: Record<string, TestSendOffer>;
  /** CamelCase for Connect API v1; sourced from frame variant `product_grids`. */
  productGrids?: Record<string, TestSendProductGrid>;
};

/**
 * Maps frame variant `product_grids` (snake_case) to test-send `config.productGrids` (camelCase).
 *
 * Forwards the *stored* config verbatim — unlike `buildProductGridConfigSlice`,
 * which maps the discover model to the backend's "dynamic" items value on save.
 * Legacy frames saved before that mapping (FOG-1918) still carry
 * `items: "discover"` here until they are re-saved or data-fixed, and the
 * backend won't populate those grids.
 */
export function toTestSendProductGrids(
  productGrids: Record<string, ProductGridConfig> | undefined,
): Record<string, TestSendProductGrid> | undefined {
  if (!productGrids || Object.keys(productGrids).length === 0) {
    return undefined;
  }
  const out: Record<string, TestSendProductGrid> = {};
  for (const [key, grid] of Object.entries(productGrids)) {
    out[key] = {
      items: grid.items,
      maxProducts: grid.max_products,
      ...(grid.static_products ? { staticProducts: grid.static_products } : {}),
      ...(grid.reviews
        ? {
            reviews: {
              maxReviews: grid.reviews.max_reviews,
              minScore: grid.reviews.min_score,
              verifiedBuyerOnly: grid.reviews.verified_buyer_only,
              sortBy: grid.reviews.sort_by,
            },
          }
        : {}),
    };
  }
  return out;
}

/** Request body for POST .../sites/:siteId/preview/test-send (Connect API v1). */
export type TestSendBody = {
  medium: string;
  /** HTML email body. */
  contentText: string;
  /** Plain-text body; sent instead of contentText for true-plaintext variants. */
  contentPlaintext?: string;
  config: TestSendConfig;
  email: string[];
  phone: string[];
  /**
   * Ignored by Connect: test-sends always use a fixed server-side customer id,
   * and the personalization profile is `preview_fill_params.user_id`. Still sent
   * until the field is deprecated in the proto.
   */
  customerId: string;
  /** Optional from/reply overrides (FOG-1616); omitted fields use site defaults. */
  fromName?: string;
  fromEmail?: string;
  replyTo?: string;
  /** Optional ENG-1291 personalization (snake_case JSON key). */
  preview_fill_params?: PreviewFillParams;
};

/** Maps test-email payload header fields to Connect test-send camelCase keys. */
export function emailHeaderOverridesForTestSend(
  payload: Pick<TestEmailPayload, "from_name" | "from_email" | "reply_to">,
): Pick<TestSendBody, "fromName" | "fromEmail" | "replyTo"> {
  const fromName = payload.from_name?.trim();
  const fromEmail = payload.from_email?.trim();
  const replyTo = payload.reply_to?.trim();
  return {
    ...(fromName ? { fromName } : {}),
    ...(fromEmail ? { fromEmail } : {}),
    ...(replyTo ? { replyTo } : {}),
  };
}

/** Parsed Connect test-send response provenance (ENG-1291). */
export type TestSendResponse = {
  used_fallback_data?: boolean;
  fallback_reason?: string;
};

export function parseTestSendResponse(data: unknown): TestSendResponse {
  if (!data || typeof data !== "object") {
    return {};
  }
  const root = data as Record<string, unknown>;
  const used =
    typeof root.used_fallback_data === "boolean"
      ? root.used_fallback_data
      : undefined;
  const reason =
    typeof root.fallback_reason === "string" && root.fallback_reason.length > 0
      ? root.fallback_reason
      : undefined;
  return {
    ...(used !== undefined ? { used_fallback_data: used } : {}),
    ...(reason !== undefined ? { fallback_reason: reason } : {}),
  };
}

/** Context for the test-email UI (subject, headers, contact); not all fields map 1:1 to the API body. */
export type TestEmailPayload = {
  to?: string[];
  send_to_self?: boolean;
  subject?: string;
  preheader?: string;
  preheader_visible?: boolean;
  reply_to?: string;
  from_email?: string;
  from_name?: string;
  session_id?: string;
  /** Maps to `customerId` on the test-send request when set. */
  contact_id?: string;
  /** HTML for test-send `contentText` when the editor does not pass inline body. */
  body?: string;
  /** Plain-text body for true-plaintext variants; wins over `body`. */
  plaintext?: string;
  /** Maps to test-send `config.offers`. */
  offers?: Record<string, TestSendOffer>;
  /** Maps to test-send `config.productGrids` when set. */
  product_grids?: Record<string, ProductGridConfig>;
  /** Maps to test-send `phone`. */
  phone?: string[];
  /** Maps to test-send `medium` (defaults to `"email"` in the hook). */
  medium?: string;
};

const CONNECT_API_V1_URL: string =
  import.meta.env.VITE_CONNECT_API_V1_URL || "";

export async function sendPreviewTestEmail(
  siteId: SiteId,
  body: TestSendBody,
): Promise<TestSendResponse> {
  const { preview_fill_params: fillParams, ...rest } = body;
  const payload: Record<string, unknown> = { ...rest };
  if (fillParams) {
    payload.preview_fill_params = fillParams;
  }
  const response = await withJWTClient((client) =>
    client.post(
      `${CONNECT_API_V1_URL}/sites/${siteId}/preview/test-send`,
      payload,
    ),
  );
  return parseTestSendResponse(response.data);
}

export function useSendTestEmailFromPayload(
  siteId: SiteId,
  frameVariantId: string,
  previewFillParams?: PreviewFillParams,
) {
  const { enqueueSnackbar } = useSnackbar();
  const ctxParts = previewUserContextKeyParts(previewFillParams);

  return useMutation<TestSendResponse, Error, TestSendBody>({
    mutationKey: ["sendTestEmail", siteId, frameVariantId, ...ctxParts],
    mutationFn: async (body) => {
      return sendPreviewTestEmail(siteId, body);
    },
    onSuccess: (data) => {
      if (data.used_fallback_data) {
        enqueueSnackbar(
          "Test email sent, but sample data was used for personalization.",
          { variant: "warning" },
        );
      } else {
        enqueueSnackbar("Test email sent successfully", { variant: "success" });
      }
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to send test email");
    },
  });
}

export type EmailSendPreview = {
  html: string;
  /**
   * Plain-text MIME part of the sent email (FOG-2299). Present once the
   * backend archives both parts; a true-plaintext send has this with an
   * empty `html`.
   */
  plaintext?: string | null;
  subject_line: string;
  preview_text: string;
};

const EMAIL_SENDS_BUCKET_URL = import.meta.env.DEV
  ? "/email-sends"
  : (import.meta.env.VITE_EMAIL_SENDS_BUCKET_URL as string) ||
    "https://storage.googleapis.com/queen-one-email-sends";

export const getEmailSendPreview = async (
  siteId: string,
  messageId: string,
): Promise<EmailSendPreview> => {
  const url = `${EMAIL_SENDS_BUCKET_URL}/${siteId}/${messageId}.json`;
  // eslint-disable-next-line demo-guard/no-unguarded-requests -- read-only bucket fetch
  const response = await fetch(url);
  if (!response.ok)
    throw new Error(`Failed to fetch email preview: ${response.status}`);
  return response.json() as Promise<EmailSendPreview>;
};

export const emailSendPreviewKeys = {
  detail: (siteId: string, messageId: string) => [
    "emailSendPreview",
    siteId,
    messageId,
  ],
};

export function useEmailSendPreview(
  siteId: string,
  messageId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: emailSendPreviewKeys.detail(siteId, messageId ?? ""),
    queryFn: () => getEmailSendPreview(siteId, messageId!),
    enabled: !!messageId && (options?.enabled ?? true),
  });
}

export type TextSendPreview = {
  medium: string;
  content: TextComposerPreviewContent;
};

const TEXT_SENDS_BUCKET_URL = import.meta.env.DEV
  ? "/text-sends"
  : (import.meta.env.VITE_TEXT_SENDS_BUCKET_URL as string) ||
    "https://storage.googleapis.com/queen-one-production-text-sends";

export const getTextSendPreview = async (
  siteId: string,
  messageId: string,
): Promise<TextSendPreview> => {
  const url = `${TEXT_SENDS_BUCKET_URL}/${siteId}/${messageId}.json`;
  // eslint-disable-next-line demo-guard/no-unguarded-requests -- read-only bucket fetch
  const response = await fetch(url);
  if (!response.ok)
    throw new Error(`Failed to fetch text preview: ${response.status}`);
  const json: unknown = await response.json();
  return json as TextSendPreview;
};

export const textSendPreviewKeys = {
  detail: (siteId: string, messageId: string) => [
    "textSendPreview",
    siteId,
    messageId,
  ],
};

/**
 * Shared options for a single archived text send. The archive is immutable once
 * written, and a 404 (no archive for this message) is an expected outcome rather
 * than a failure — without `retry: false` the global predicate in `src/query.ts`
 * treats the plain `Error` thrown here as a network error and retries it 3x.
 */
export const textSendPreviewQueryOptions = (
  siteId: string,
  messageId: string,
) => ({
  queryKey: textSendPreviewKeys.detail(siteId, messageId),
  queryFn: () => getTextSendPreview(siteId, messageId),
  staleTime: Infinity,
  gcTime: 60 * 60 * 1000,
  retry: false,
});

export function useTextSendPreview(
  siteId: string,
  messageId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...textSendPreviewQueryOptions(siteId, messageId ?? ""),
    enabled: !!messageId && (options?.enabled ?? true),
  });
}
